import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { RouteComponentProps } from '@reach/router';

import Layout from '../../../components/Layout';
import {
  DEFAULT_MIGRATION_SCOPE,
  MigrationScopeOption,
  describeReturnedScope,
  isAccessPointMigrationMonitorEnabled,
  scopeLabel,
  scopeToIsStaging,
} from './config';
import {
  AccessPointMigrationMutation,
  AccessPointMigrationResult,
  AccessPointMigrationStatusQuery,
  AccessPointMigrationVariables,
  GET_ACCESS_POINT_MIGRATION_STATUS,
  RUN_ACCESS_POINT_MIGRATION,
} from './query';

interface AccessPointMigrationProps extends RouteComponentProps {}

const cardProps = {
  bg: 'gray.800',
  borderWidth: '1px',
  borderColor: 'gray.700',
  rounded: 'md',
  p: '4',
} as const;

function KpiCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <Box {...cardProps}>
      <Text fontSize="11px" fontWeight="medium" fontFamily="mono" textTransform="uppercase" letterSpacing="wider" color="gray.500">
        {label}
      </Text>
      <Text fontSize="2xl" fontWeight="semibold" color={color || 'white'} lineHeight="1.2" fontFamily="mono">
        {value}
      </Text>
      {sub ? (
        <Text fontSize="12px" color="gray.500" mt="1">
          {sub}
        </Text>
      ) : null}
    </Box>
  );
}

function ScopeBadge({ scope, isStaging }: { scope?: string | null; isStaging?: boolean | null }) {
  const label = describeReturnedScope(scope, isStaging);
  const colorScheme = scope === 'PRODUCTION' ? 'red' : scope === 'STAGING' ? 'purple' : 'gray';
  return <Badge colorScheme={colorScheme}>{label}</Badge>;
}

function ResultSummary({ result }: { result: AccessPointMigrationResult }) {
  return (
    <Box mt="4" p="3" rounded="md" bg="gray.800" borderWidth="1px" borderColor="gray.700">
      <Flex gap="2" mb="2" flexWrap="wrap">
        <Badge colorScheme={result.dryRun ? 'blue' : 'green'}>{result.dryRun ? 'Dry Run' : 'Mutation'}</Badge>
        {result.force ? <Badge colorScheme="orange">Force</Badge> : null}
        <ScopeBadge scope={result.scope} isStaging={result.isStaging} />
      </Flex>
      <Grid templateColumns={['1fr 1fr', 'repeat(3, 1fr)']} gap="2" fontSize="sm">
        <Text>Processed: {result.processed}</Text>
        <Text>Migrated: {result.migrated}</Text>
        <Text>Skipped: {result.skippedAlreadyMigrated}</Text>
        <Text>Missing: {result.missingDropPoint}</Text>
        <Text>Failed: {result.failed}</Text>
        <Text>Differing: {result.differing}</Text>
      </Grid>
      {result.failures.length > 0 ? (
        <Box mt="3">
          <Text fontWeight="bold" fontSize="sm" mb="1">
            Failures
          </Text>
          {result.failures.slice(0, 10).map((failure) => (
            <Text key={`${failure.accessPointId}-${failure.reason}`} fontSize="xs" color="red.300">
              {failure.accessPointId}: {failure.reason}
            </Text>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

function DisabledState({ title, message }: { title: string; message: string }) {
  return (
    <Layout fullWidth>
      <Box p="4" bg="gray.800" rounded="md" borderWidth="1px" borderColor="gray.700" maxW="640px">
        <Text fontSize="lg" fontWeight="bold" color="white" mb="2">
          {title}
        </Text>
        <Text color="gray.400" fontSize="sm">
          {message}
        </Text>
      </Box>
    </Layout>
  );
}

export const AccessPointMigration: React.FC<AccessPointMigrationProps> = () => {
  const monitorEnabled = isAccessPointMigrationMonitorEnabled();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [dryRun, setDryRun] = useState(true);
  const [force, setForce] = useState(false);
  const [limit, setLimit] = useState('');
  const [scope, setScope] = useState<MigrationScopeOption>(DEFAULT_MIGRATION_SCOPE);
  const [result, setResult] = useState<AccessPointMigrationResult | null>(null);

  const { data, loading, error, refetch } = useQuery<AccessPointMigrationStatusQuery>(GET_ACCESS_POINT_MIGRATION_STATUS, {
    fetchPolicy: 'network-only',
    variables: { isStaging: scopeToIsStaging(scope) },
    // Fail closed against a mismatched/old backend that doesn't support the
    // new args/fields: keep partial data instead of throwing.
    errorPolicy: 'all',
    skip: !monitorEnabled,
  });
  const [runMigration, { loading: running }] = useMutation<AccessPointMigrationMutation>(RUN_ACCESS_POINT_MIGRATION);

  const status = data?.internalAccessPointMigrationStatus;
  const percent = status?.totalLinkedDatasets ? Math.round((status.migratedCount / status.totalLinkedDatasets) * 100) : 0;

  const changeScope = async (nextScope: MigrationScopeOption) => {
    setScope(nextScope);
    if (!monitorEnabled) return;
    try {
      await refetch({ isStaging: scopeToIsStaging(nextScope) });
    } catch {
      // Swallow: rendered error/disabled state below handles surfacing this.
    }
  };

  const handleRun = async () => {
    const parsedLimit = limit.trim() ? Number(limit) : undefined;
    const variables: AccessPointMigrationVariables = {
      dryRun,
      force,
      limit: Number.isFinite(parsedLimit) ? parsedLimit : undefined,
      isStaging: scopeToIsStaging(scope),
    };
    const response = await runMigration({ variables });
    setResult(response.data?.internalMigrateAccessPointDataToStations || null);
    try {
      await refetch({ isStaging: scopeToIsStaging(scope) });
    } catch {
      // Ignore refetch failures; the mutation result is already displayed.
    }
  };

  // Requirement 1: fail closed/gracefully when the feature flag is off so a
  // mismatched deployment can't break an old CRM session via this route.
  if (!monitorEnabled) {
    return (
      <DisabledState
        title="AccessPoint Migration deaktiviert"
        message="Der Migration-Monitor ist in dieser Umgebung deaktiviert. Setze REACT_APP_ENABLE_ACCESS_POINT_MIGRATION_MONITOR=true, um ihn zu aktivieren."
      />
    );
  }

  if (loading && !status) {
    return (
      <Layout fullWidth>
        <Flex justifyContent="center" alignItems="center" minH="300px">
          <Spinner />
        </Flex>
      </Layout>
    );
  }

  // Requirement 3: if the status query errors (e.g. old backend missing the new
  // `isStaging` arg / `scope` field), show a helpful error state, not a crash.
  if (error && !status) {
    return (
      <DisabledState
        title="Migrationsstatus konnte nicht geladen werden"
        message="Der Status konnte nicht geladen werden. Dies passiert typischerweise, wenn das Backend die neuen Migrations-Felder (isStaging/scope) noch nicht unterstützt. Bitte Backend-Version prüfen."
      />
    );
  }

  return (
    <Layout fullWidth>
      <Flex alignItems="center" justifyContent="space-between" mb="4" gap="3" flexWrap="wrap">
        <Box>
          <Text as="h2" fontSize="2xl" fontWeight="bold" color="white">
            AccessPoint Migration
          </Text>
          <Text color="gray.400" fontSize="sm">
            PackageStation-owned verification and opening-hours migration status.
          </Text>
        </Box>
        <Button colorScheme="brand" onClick={onOpen}>
          Migration ausführen
        </Button>
      </Flex>

      <Flex alignItems="center" gap="2" mb="4" flexWrap="wrap">
        <Text fontSize="sm" color="gray.400">
          Aktueller Scope:
        </Text>
        <ScopeBadge scope={status?.scope} isStaging={status?.isStaging} />
        {error ? (
          <Badge colorScheme="yellow" title="Teilweise geladen – Backend-Felder ggf. nicht verfügbar">
            Teildaten
          </Badge>
        ) : null}
      </Flex>

      <Grid templateColumns={['1fr 1fr', 'repeat(4, 1fr)']} gap="3" mb="4">
        <KpiCard label="Total" value={status?.totalLinkedDatasets || 0} />
        <KpiCard label="Migrated" value={status?.migratedCount || 0} color="green.300" />
        <KpiCard label="Unmigrated" value={status?.unmigratedCount || 0} color="yellow.300" />
        <KpiCard label="Missing" value={status?.missingDropPointCount || 0} color="red.300" />
      </Grid>

      <Box {...cardProps} mb="4">
        <Flex alignItems="center" justifyContent="space-between" mb="2">
          <Text fontWeight="bold">Progress</Text>
          <Text fontFamily="mono">{percent}%</Text>
        </Flex>
        <Progress value={percent} colorScheme={percent === 100 ? 'green' : 'blue'} rounded="md" />
      </Box>

      <Grid templateColumns={['1fr', 'repeat(4, 1fr)']} gap="3">
        <KpiCard label="Differing" value={status?.differingCount || 0} />
        <KpiCard label="Opening Hours Diff" value={status?.openingHoursDifferingCount || 0} />
        <KpiCard label="Identity Diff" value={status?.identityDifferingCount || 0} />
        <KpiCard label="Failed" value={status?.failedCount ?? 'n/a'} />
      </Grid>

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent bg="gray.900" color="white">
          <ModalHeader>AccessPoint-Daten migrieren</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.300" mb="4">
              Führt die Migration von AccessPoint-owned Feldern nach PackageStation aus. Dry Run ist vorausgewählt.
            </Text>
            <Flex direction="column" gap="3">
              <Box>
                <Text fontSize="sm" mb="1">
                  Ziel-Scope
                </Text>
                <RadioGroup value={scope} onChange={(value) => changeScope(value as MigrationScopeOption)}>
                  <Stack spacing="1">
                    <Radio value="STAGING">{scopeLabel('STAGING')}</Radio>
                    <Radio value="PRODUCTION">{scopeLabel('PRODUCTION')}</Radio>
                    <Radio value="ALL">{scopeLabel('ALL')}</Radio>
                  </Stack>
                </RadioGroup>
                {scope === 'ALL' ? (
                  <Text fontSize="xs" color="orange.300" mt="1">
                    Achtung: „Alle AccessPoints“ umfasst auch Produktion.
                  </Text>
                ) : null}
              </Box>
              <Checkbox isChecked={dryRun} onChange={(event) => setDryRun(event.target.checked)}>
                Dry Run
              </Checkbox>
              <Checkbox isChecked={force} onChange={(event) => setForce(event.target.checked)}>
                Force bereits migrierte Datensätze erneut kopieren
              </Checkbox>
              <Box>
                <Text fontSize="sm" mb="1">
                  Optionales Limit
                </Text>
                <Input value={limit} onChange={(event) => setLimit(event.target.value)} placeholder="z.B. 100" type="number" />
              </Box>
            </Flex>
            {result ? <ResultSummary result={result} /> : null}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={onClose}>
              Schließen
            </Button>
            <Button colorScheme={dryRun ? 'blue' : 'red'} onClick={handleRun} isLoading={running}>
              {dryRun ? 'Dry Run starten' : 'Migration starten'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Layout>
  );
};

export default AccessPointMigration;
